import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Col, Row } from 'reactstrap';

import {
  EXPORTS, IMPORTS, SectionHeader, SectionWrapper,
} from 'src/enosikit/components/Chart/components/chartConstants';
import {
  buildTradeTooltipGroups,
  extractTradeValues, getDirectionLabel, getTooltipFormatFunc, getTooltipItemValue,
  isOtherTooltipItemKey,
} from 'src/enosikit/components/Chart/helpers/tooltip';
import {
  BUY, DATA_AGGREGATE_BY_PORTFOLIO, DATA_AGGREGATE_BY_PROPERTY,
  DATA_GROUP_BY_COUNTERPARTY, DATA_GROUP_BY_TRADE_TYPE, SELL,
  TRADE_TYPE_ORDER,
} from 'src/util/constants';
import { getTradeTypeLabel } from 'src/util/i18n/constants';

/**
 * Resolves the display label for a single trade tooltip line item.
 * @param {import('react-intl').IntlShape} intl i18n intl object.
 * @param {object} datum Tooltip datum for one series.
 * @param {DATA_GROUP_BY_COUNTERPARTY|string} groupBy Active grouping option.
 * @param {boolean} aggByProperty Whether the chart aggregates by property.
 * @returns {string|null} Line item label.
 */
export const formatTradeItemLabel = (intl, datum, groupBy, aggByProperty) => {
  const {
    key, label, property, subLabel, counterPartyUser, counterPartyProperty, tradeType,
  } = datum;

  if (isOtherTooltipItemKey(key)) {
    return label;
  }

  // A counterparty property always identifies the counterparty, so it takes
  // precedence over the source property when grouping by counterparty.
  if (groupBy === DATA_GROUP_BY_COUNTERPARTY && counterPartyProperty) {
    return counterPartyProperty.title || getTradeTypeLabel(intl, tradeType);
  }

  if (aggByProperty && property?.title) {
    return counterPartyUser && subLabel
      ? `${property.title} (${subLabel})`
      : property.title;
  }

  if (groupBy === DATA_GROUP_BY_COUNTERPARTY) {
    if (counterPartyUser) {
      return subLabel || getTradeTypeLabel(intl, tradeType);
    }
    return getTradeTypeLabel(intl, tradeType || label);
  }

  return getTradeTypeLabel(intl, aggByProperty ? subLabel : label);
};

/**
 * Renders a single tooltip line, optionally indented as a nested sub-item.
 * @param {object} props Component props.
 * @param {React.ReactNode} props.label Line label.
 * @param {React.ReactNode} props.value Formatted line value.
 * @param {boolean} props.nested Whether the line is a nested sub-item.
 * @returns {React.ReactElement} Tooltip line.
 */
function TradeTooltipRow({ label = null, value = null, nested = false }) {
  return (
    <Row className="mb-1">
      <Col className={nested ? 'pe-0 ps-4' : 'pe-0'}>
        <span>{label}</span>
      </Col>
      <Col xs="auto">{value}</Col>
    </Row>
  );
}

TradeTooltipRow.propTypes = {
  label: PropTypes.node,
  nested: PropTypes.bool,
  value: PropTypes.node,
};

/**
 * Renders the trade-type groups of one tooltip section. Each group renders a
 * trade-type total, followed by indented sub-rows unless grouping by trade type
 * at the portfolio level, in which case the total already conveys the value and
 * the sub-rows are omitted.
 * @param {object} props Component props.
 * @param {Array<object>} props.items Tooltip items belonging to the section.
 * @param {object} props.chartView Chart aggregation and grouping options.
 * @param {Function} props.tooltipFormatFunc Value formatter.
 * @param {string} props.unitLabel Unit display label.
 * @returns {React.ReactElement} Section body.
 */
function TradeTooltipGroups({
  items, chartView, tooltipFormatFunc, unitLabel = null,
}) {
  const intl = useIntl();
  const { aggregateBy, groupBy } = chartView;
  const aggByProperty = aggregateBy === DATA_AGGREGATE_BY_PROPERTY;
  // When grouping by trade type at the portfolio level, the group total already
  // conveys the trade-type value, so the nested sub-rows would only repeat it.
  // Suppress them to keep the tooltip to a single line per trade type.
  const showNestedItems = !(groupBy === DATA_GROUP_BY_TRADE_TYPE
    && aggregateBy === DATA_AGGREGATE_BY_PORTFOLIO);
  // `items` is referentially stable across mousemove-only re-renders while the
  // pointer stays over the same bar (see `getTooltipDataFunc` in ChartTrade),
  // so this skips the O(N log N) grouping/sorting work on those re-renders.
  const groups = useMemo(() => buildTradeTooltipGroups(
    items,
    {
      excludeZero: true,
      tradeTypeOrder: TRADE_TYPE_ORDER,
    },
  ), [items]);

  return (
    <>
      {groups.map((group) => {
        const groupLabel = getTradeTypeLabel(intl, group.tradeType);

        return (
          <React.Fragment key={group.tradeType}>
            <TradeTooltipRow
              label={groupLabel}
              value={tooltipFormatFunc(group.total, unitLabel)}
            />
            {showNestedItems && group.items.map((datum) => (
              <TradeTooltipRow
                key={datum.key}
                nested
                label={formatTradeItemLabel(intl, datum, groupBy, aggByProperty)}
                value={tooltipFormatFunc(datum.value, unitLabel)}
              />
            ))}
          </React.Fragment>
        );
      })}
    </>
  );
}

TradeTooltipGroups.propTypes = {
  chartView: PropTypes.shape({
    aggregateBy: PropTypes.string,
    groupBy: PropTypes.string,
  }).isRequired,
  items: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  tooltipFormatFunc: PropTypes.func.isRequired,
  unitLabel: PropTypes.string,
};

/**
 * Renders the tooltip content for a trade chart.
 * @param {object} props Component props.
 * @param {object} props.data Tooltip datapack.
 * @param {string} props.unitLabel Unit display label.
 * @param {object} props.chartView Chart aggregation and grouping options.
 * @param {string} props.unit Chart unit.
 * @returns {React.ReactElement|null} Trade tooltip content.
 */
export default function TradeChartTooltip({
  data = null, unitLabel = null, chartView = null, unit = null,
}) {
  const intl = useIntl();

  // `data` is referentially stable across mousemove-only re-renders while the
  // pointer stays over the same bar (see `getTooltipDataFunc` in ChartTrade),
  // so memoizing per-direction items on it keeps their references stable too,
  // letting `TradeTooltipGroups`'s `useMemo` actually skip recomputing groups
  // on those re-renders. Computed unconditionally (before the `!data` early
  // return below) so the hook call order stays stable across renders.
  const itemsByDirection = useMemo(() => Object.fromEntries(
    [BUY, SELL].map((direction) => [
      direction,
      Object.values(data?.[direction] || {})
        .filter(Boolean)
        .filter((item) => getTooltipItemValue(item.value) !== 0),
    ]),
  ), [data]);

  if (!data || !chartView) {
    return null;
  }

  const values = extractTradeValues(data);
  const tooltipFormatFunc = getTooltipFormatFunc(unit, values);

  return (
    <>
      {[BUY, SELL].map((direction) => {
        const items = itemsByDirection[direction];
        if (items.length === 0) {
          return null;
        }

        const tooltipLabel = direction === BUY
          ? getDirectionLabel(IMPORTS, intl) : getDirectionLabel(EXPORTS, intl);

        return (
          <SectionWrapper key={direction}>
            <SectionHeader>{tooltipLabel}</SectionHeader>
            <TradeTooltipGroups
              chartView={chartView}
              items={items}
              tooltipFormatFunc={tooltipFormatFunc}
              unitLabel={unitLabel}
            />
          </SectionWrapper>
        );
      })}
    </>
  );
}

TradeChartTooltip.propTypes = {
  chartView: PropTypes.shape({}),
  data: PropTypes.shape({}),
  unit: PropTypes.string,
  unitLabel: PropTypes.string,
};
